import {
  PlusOutlined,
  EditOutlined,
  SafetyCertificateOutlined,
  AppstoreOutlined,
  FileOutlined,
  ApiOutlined,
  ThunderboltOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import AddHabilitation from "./AddHabilitation";
import ExportableFilterableTable from "../../components/tables/ExportableFilterableTable";
import { COLORS } from "../../components/ui/colors";
import axiosInstance from "../../variables/api/axiosInstance";

const { Title, Text } = Typography;

// Couleur de tag par module (reprend la convention de l'ancien ecran)
const MODULE_TAG_COLOR = {
  "Gestion du personnel": "default",
  "Gestion de la production": "blue",
  "Gestion des sinistres": "red",
  "Gestion de la comptabilité": "green",
  "Statistiques et Etats": "gold",
  "Gestion des paramètres": "purple",
  "Gestion des clients": "cyan",
  "Gestion des accès": "orange",
  "Gestion des membres": "geekblue",
  "Gestion des opérations": "magenta",
};
const moduleColor = (n) => MODULE_TAG_COLOR[n] || "default";

// Couleur + icone par type d'habilitation
const TYPE_META = {
  PAGE: { color: "blue", icon: <FileOutlined /> },
  API: { color: "geekblue", icon: <ApiOutlined /> },
  ACTION: { color: "volcano", icon: <ThunderboltOutlined /> },
};

const Habilitations = () => {
  const [habilitations, setHabilitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [selectedHabilitation, setSelectedHabilitation] = useState("");
  const [showModal, setShowModal] = useState(false);

  const handleShowCreate = () => {
    setEditMode(false);
    setSelectedHabilitation("");
    setShowModal(true);
  };
  const handleClose = () => setShowModal(false);

  const handleEdit = (record) => {
    setEditMode(true);
    setSelectedHabilitation(record);
    setShowModal(true);
  };

  useEffect(() => {
    setLoading(true);
    axiosInstance
      .get(`/api/habilitation`)
      .then((res) => {
        setHabilitations(Array.isArray(res.data) ? res.data : []);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, [showModal]);

  const moduleFilterOptions = useMemo(
    () =>
      Array.from(new Set(habilitations.map((h) => h.module?.nom).filter(Boolean)))
        .sort()
        .map((nom) => ({ label: nom, value: nom })),
    [habilitations]
  );

  const typeFilterOptions = useMemo(
    () =>
      Array.from(new Set(habilitations.map((h) => h.type).filter(Boolean))).map((t) => ({
        label: t,
        value: t,
      })),
    [habilitations]
  );

  const columns = [
    {
      title: "Code",
      dataIndex: "codeHabilitation",
      key: "codeHabilitation",
      searchable: true,
      width: 120,
      render: (code) => (
        <Tag color="default" style={{ fontFamily: "monospace", borderRadius: 6 }}>
          {code}
        </Tag>
      ),
    },
    {
      title: "Intitulé",
      dataIndex: "libelle",
      key: "libelle",
      searchable: true,
      render: (_, record) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: COLORS.secondary }}>{record.libelle}</div>
          {record.description && (
            <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              {record.description}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      searchable: true,
      searchValue: (row) => row.module?.nom,
      filterType: "select",
      filterOptions: moduleFilterOptions,
      filterMatcher: (cellValue, filterValue) => cellValue?.nom === filterValue,
      exportValue: (row) => row.module?.nom || "",
      render: (module) =>
        module?.nom ? (
          <Tooltip title={module.nom}>
            <Tag icon={<AppstoreOutlined />} color={moduleColor(module.nom)} style={{ borderRadius: 6 }}>
              {module.nom}
            </Tag>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      searchable: true,
      filterType: "select",
      filterOptions: typeFilterOptions,
      width: 130,
      render: (type) => {
        const meta = TYPE_META[type] || { color: "default", icon: null };
        return type ? (
          <Tag icon={meta.icon} color={meta.color} style={{ borderRadius: 6 }}>
            {type}
          </Tag>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: "Actions",
      key: "action",
      hideInExport: true,
      width: 90,
      align: "center",
      render: (_, record) => (
        <Tooltip title="Modifier">
          <Button
            type="text"
            shape="circle"
            icon={<EditOutlined style={{ color: COLORS.primary }} />}
            onClick={() => handleEdit(record)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu={"DROITS"} />

        <div className="layout-page">
          <NavBar />

          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              {/* En-tete */}
              <Card
                bordered={false}
                style={{
                  borderRadius: 16,
                  marginBottom: 20,
                  background: COLORS.gradient,
                  boxShadow: "0 6px 20px rgba(36,33,102,0.25)",
                }}
                bodyStyle={{ padding: "22px 28px" }}
              >
                <Row align="middle" justify="space-between" gutter={[16, 16]}>
                  <Col>
                    <Space align="center" size="middle">
                      <div
                        style={{
                          width: 56, height: 56, borderRadius: 14,
                          background: "rgba(255,255,255,0.18)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                      >
                        <SafetyCertificateOutlined style={{ fontSize: 28, color: "#fff" }} />
                      </div>
                      <div>
                        <Title level={3} style={{ color: "#fff", margin: 0 }}>
                          Gestion des habilitations
                        </Title>
                        <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                          Droits d'accès aux pages, actions et API de l'application
                        </Text>
                      </div>
                    </Space>
                  </Col>
                  <Col>
                    <Button
                      size="large"
                      icon={<PlusOutlined />}
                      onClick={handleShowCreate}
                      style={{
                        background: "#fff",
                        color: COLORS.secondary,
                        fontWeight: 600,
                        border: "none",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                      }}
                    >
                      Ajouter une habilitation
                    </Button>
                  </Col>
                </Row>
              </Card>

              {/* Tableau */}
              <Card
                bordered={false}
                title={
                  <Space>
                    <KeyOutlined style={{ color: COLORS.primary }} />
                    <span>Liste des habilitations</span>
                  </Space>
                }
                extra={<Tag color="blue">{habilitations.length} habilitation{habilitations.length > 1 ? "s" : ""}</Tag>}
                style={{ borderRadius: 16, boxShadow: "0 4px 16px rgba(0,0,0,0.06)" }}
                bodyStyle={{ paddingTop: 12 }}
              >
                <ExportableFilterableTable
                  columns={columns}
                  dataSource={habilitations}
                  exportDataSource={habilitations}
                  rowKey="habilitationId"
                  loading={loading}
                  searchPlaceholder="Rechercher une habilitation..."
                  exportFileName="habilitations"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total : ${total} habilitation(s)`,
                  }}
                  expandable={{
                    expandedRowRender: (record) => (
                      <div style={{ padding: "4px 8px" }}>
                        <Text type="secondary" style={{ fontWeight: 600 }}>
                          Description :{" "}
                        </Text>
                        <Text>{record.description || "—"}</Text>
                        {record.route?.path && (
                          <div style={{ marginTop: 4 }}>
                            <Text type="secondary" style={{ fontWeight: 600 }}>
                              Route :{" "}
                            </Text>
                            <Text code>{record.route.path}</Text>
                          </div>
                        )}
                      </div>
                    ),
                  }}
                />
              </Card>

              <Footer />
            </div>
          </div>
        </div>
      </div>

      <AddHabilitation
        show={showModal}
        handleClose={handleClose}
        editMode={editMode}
        selectedHabilitation={selectedHabilitation}
      />

      <div className="layout-overlay layout-menu-toggle"></div>
      <div className="drag-target"></div>
    </div>
  );
};

export default Habilitations;
